import React, { useEffect, useState, useCallback } from "react";
import { Layout, Menu, Button, Drawer, Select, Row, Col, Tag, Avatar, Tooltip, message } from "antd";
import { MenuOutlined } from '@ant-design/icons';
import { hotjar } from 'react-hotjar';
import moment from "moment";
import { useTranslation } from "react-i18next";
import { HasAccess, usePermify } from '@permify/react-role';
import { Link, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import "./privateLayout.less";
import { getBranchOffices, getBoxes } from "../services/general";
import { removeCookie, getCookie, setCookie } from "../services/cookies";
import { getActiveProvidersByBranches, setActiveProviders } from "../services/providers";
import { logout } from "../services/helper";
import LogoNaranjo2023 from '../images/LogoNaranjo2023.svg';
import { useBoxContext } from "../context/BoxContext";

const { Option } = Select;
const { Header, Content } = Layout;

const PrivateLayout = ({ children }) => {
  const [visibleDrawer, setVisibleDrawer] = useState({
    mobile: false,
    desktop: false,
  });
  const [branchOffices, setBranchOffices] = useState();
  const [branchOffice, setBranchOffice] = useState();
  const [isPopupRoute, setIsPopupRoute] = useState(false);
  const { setUser } = usePermify();
  const location = useLocation();
  const avatarName = getCookie('email') ? getCookie('email').substring(0, 2).toUpperCase() : null;
  const countryCode = getCookie("country_code");
  const { t } = useTranslation();
  const [boxes, setBoxes] = useState([]);
  const [selectedBox, setSelectedBox] = useState();
  const { setHasBoxes } = useBoxContext();

  const updateBoxContext = useCallback((boxes = []) => {
    const hasBoxes = boxes.length > 0;
    setCookie("branch_office_has_boxes", hasBoxes, { path: '/' });
    setHasBoxes(hasBoxes);
  }, [setHasBoxes]);

  useEffect(() => {
    if (branchOffice?.id) {
      const fetchBoxes = async () => {
        try {
          const response = await getBoxes({ BranchOfficeId: branchOffice.id });
          const boxes = response.boxes || [];
          setBoxes(boxes);
          const savedBoxId = getCookie("box_id");
          const foundBox = boxes.find(b => b.id === savedBoxId);
          setSelectedBox(foundBox || null);
          updateBoxContext(boxes);
        } catch (error) {
          updateBoxContext([]);
          console.error('Error cargando boxes:', error);
        }
      };
      fetchBoxes();
    }
  }, [branchOffice, updateBoxContext]);


  const handleBranchOffice = async (value) => {
    if (value) {
      setCookie("branch_office_id", value);
      setBranchOffice(branchOffices.find(branchOffice => branchOffice.id === value));
    }
    handleDrawersVisibility('desktop');

    removeCookie("box_id", { path: '/' });
    removeCookie("branch_office_has_boxes", { path: '/' });
    setBoxes([]);
    setSelectedBox(null);
    try {
      const response = await getBoxes({ BranchOfficeId: value });
      setBoxes(response.boxes || []);
      updateBoxContext(boxes);
    } catch (error) {
      updateBoxContext([]);
      console.error('Error recargando boxes al cambiar sucursal:', error);
    }
    handleDrawersVisibility('desktop');
  };

  const handleBox = (value) => {
    if (value) {
      setCookie("box_id", value);
      const box = boxes.find(b => b.id === value);
      if (box) {
        setSelectedBox(box);
      }
    }
    handleDrawersVisibility('desktop');
  };

  const fetchBranchOffices = useCallback(async () => {
    try {
      const response = await getBranchOffices({
        byUser: 1,
      });

      if (response?.length) {
        setBranchOffices(response);

        if (getCookie('branch_office_id')) {
          setBranchOffice(response.find(branchOffice => {
            return branchOffice.id === parseInt(getCookie('branch_office_id'))
          }));
        }
      }
    } catch (error) {
      console.error('Error en Fetch Branch Offices', error);
      message.error({
        content: 'Error al buscar sucursales',
        style: {
          marginTop: '40vh',
        },
      });
      Sentry.captureException(error);
    }
  }, []);

  const checkPopupRoute = useCallback(() => {
    if (['/consentimientos', '/validacion', '/screenshot'].includes(location.pathname)) {
      setIsPopupRoute(true);
    }
  }, [location.pathname]);


  useEffect(() => {
    document.title = "SAGE 2.0";
    fetchBranchOffices();
    if (!getCookie("branch_office_id")) {
      removeCookie("branch_office_id", { path: '/' });
    }
    if (!getCookie("box_id")) {
      removeCookie("box_id", { path: '/' });
    }
    if (hotjar.initialized()) {
      hotjar.identify(getCookie("email"));
    }
  }, [fetchBranchOffices]);

  useEffect(() => {
    const popupPaths = ["/consentimientos", "/validacion", "/screenshot"];
    setIsPopupRoute(popupPaths.includes(location.pathname));
  }, [checkPopupRoute, fetchBranchOffices]);

  useEffect(() => {
    setUser({
      id: getCookie('email'),
      roles: getCookie('roles'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawersVisibility = (key) => {
    setVisibleDrawer(visibleDrawer => ({ ...visibleDrawer, [key]: !visibleDrawer[key] }));
  };

 const fetchAndStoreActiveProviders = async () => {
    try {
      const activeProviders = await getActiveProvidersByBranches();
      setActiveProviders(activeProviders);
    } catch (error) {
      console.error("Error setting active providers:", error);
    }
  }

  useEffect(() => {
    fetchAndStoreActiveProviders();
  }, []);

  return (
    <Layout>
      <Header className="header">
        <div className="logo">
          <img src={LogoNaranjo2023} alt="Logo CMT salud" height={43} />
        </div>

        <div className="menu-desktop">
          <Row align="middle">
            <Col>
              {selectedBox?.id && <Tag className="cmt-tag">{selectedBox.name}</Tag>}
            </Col>
            <Col>
              {
                branchOffice && <Tag className="cmt-tag">{branchOffice.name}</Tag>
              }
            </Col>
            <Col>
              <Tooltip
                placement="bottomRight"
                title={getCookie('email')}
                color="#223A70"
              >
                <Avatar
                  size="large"
                  className="mr-5"
                >
                  {avatarName}
                </Avatar>
              </Tooltip>
            </Col>
            <Col>
              {
                !isPopupRoute && (
                  <Button
                    onClick={logout}
                    className="cmt-button primary non-outline"
                    type="primary"
                    size="small"
                  >
                    Cerrar sesión
                  </Button>
                )
              }
            </Col>
          </Row>
        </div>

        <div className="menu-mobile">
          <Button
            className="cmt-button primary non-outline"
            onClick={() => handleDrawersVisibility('mobile')}
            shape="circle"
            icon={<MenuOutlined />}
            size="large"
            type="primary"
          />
          <Drawer
            placement="right"
            closable
            open={visibleDrawer.mobile}
            onClose={() => handleDrawersVisibility('mobile')}
            className="private-layout-drawer"
          >
            <Row gutter={[0, 40]} align="middle">
              <Col xs={24}>
                <Row gutter={20} align="middle">
                  <Col>
                    {branchOffice && <Tag>{branchOffice.name}</Tag>}
                  </Col>
                  <Col>
                    {selectedBox?.id && <Tag>{selectedBox.name}</Tag>}
                  </Col>
                  <Col>
                    <Tooltip placement="left" title={getCookie('email')}>
                      <Avatar
                        style={{
                          backgroundColor: '#2f9980',
                          verticalAlign: 'middle',
                        }}
                        size="large"
                      >
                        {avatarName}
                      </Avatar>
                    </Tooltip>
                  </Col>
                </Row>
              </Col>
              <Col>
                {
                  !isPopupRoute && (
                    <Button
                      onClick={logout}
                      className="cmt-button primary non-outline"
                      type="primary"
                      size="small"
                    >
                      Cerrar sesión
                    </Button>
                  )
                }
              </Col>
            </Row>
          </Drawer>
        </div>
      </Header>

      <Layout style={{ marginTop: 68 }}>
        {
          (!visibleDrawer.desktop && !isPopupRoute) && (
            <Button
              className="cmt-button primary drawer-button"
              type="primary"
              size="large"
              onClick={() => handleDrawersVisibility('desktop')}
            >
              <span className="icon-system-menu" />
            </Button>)
        }

        <Drawer
          zIndex={200}
          width={287}
          open={visibleDrawer.desktop}
          onClose={() => handleDrawersVisibility('desktop')}
          closable={false}
          placement="left"
          className="side-drawer"
        >
          <Row>
            <Col lg={24} xs={24}>
              <p className="mb-5">{t('privateLayout.branchOffice')}</p>
              <Select
                showSearch
                filterOption={(input, option) => option?.children?.toLowerCase().includes(input?.toLowerCase())}
                className="cmt-select"
                size="large"
                defaultValue={parseInt(getCookie("branch_office_id"))}
                onChange={handleBranchOffice}
                style={{ width: "100%", marginBottom: 10 }}
                notFoundContent={<p className="mb-0 text-center">No hay datos</p>}
              >
                {
                  branchOffices?.map((branchOffice, index) => (
                    <Option key={index} value={branchOffice.id}>
                      {branchOffice.name}
                    </Option>
                  ))
                }
              </Select>
            </Col>
          </Row>

        <HasAccess
          roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
          permission="select-box"
        >
          {boxes.length > 0 && (
            <Row>
              <Col lg={24} xs={24}>
                <p className="mb-5">Box</p>
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input?.toLowerCase())
                  }
                  className="cmt-select"
                  size="large"
                  value={getCookie("box_id")}
                  onChange={handleBox}
                  style={{ width: "100%", marginBottom: 10 }}
                  placeholder={t('privateLayout.selectBox')}
                  notFoundContent={<p className="mb-0 text-center">No hay datos</p>}
                >
                  {boxes.map(({ id, name }) => (
                    <Option key={id} value={id}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          )}
        </HasAccess>

          <Menu
            mode="inline"
            defaultSelectedKeys={["1"]}
            defaultOpenKeys={["sub1"]}
            style={{ height: "100%", borderRight: 0 }}
          >
            <HasAccess
              roles={['super_usuario', 'branch_office_manager', 'administracion',
                'equipo_psicologia', 'doctor', 'direccion_medica', 'equipo_medico']}
              permission="menu-reservation-main"
            >
              <Menu.ItemGroup key="1" title={t('privateLayout.reservations')}>
                <Menu.Item key="sub11" className="mt-0 mb-0 pl-20">
                  <Link
                    to={`/reservas/fecha/${moment().format('DD-MM-YYYY')}`}
                    onClick={() => handleDrawersVisibility('desktop')}
                  >
                    {t('privateLayout.reservationsByDate')}
                  </Link>
                </Menu.Item>

                <HasAccess
                  roles={['super_usuario', 'branch_office_manager',
                    'administracion']}
                  permission="menu-reservation-search"
                >
                  <Menu.Item key="sub12" className="mt-0 mb-0 pl-20">
                    <Link
                      to="/reservas/buscar"
                      onClick={() => handleDrawersVisibility('desktop')}
                    >
                      {t('privateLayout.searchReservations')}
                    </Link>
                  </Menu.Item>
                </HasAccess>

                <HasAccess
                  roles={['super_usuario', 'branch_office_manager', 'administracion']}
                  permission="menu-reservation-web"
                >
                  <Menu.Item key="sub13" className="mt-0 mb-0 pl-20">
                    <Link
                      to="/reservas/web"
                      onClick={() => handleDrawersVisibility('desktop')}
                    >
                      {t('privateLayout.webReservations')}
                    </Link>
                  </Menu.Item>
                </HasAccess>

                <HasAccess
                  roles={['super_usuario', 'branch_office_manager', 'administracion']}
                  permission="menu-admissions"
                >
                  <Menu.Item key="sub14" className="mt-0 mb-0 pl-20">
                    <Link
                      to="/ingresos"
                      onClick={() => handleDrawersVisibility('desktop')}
                    >
                      Ingresos
                    </Link>
                  </Menu.Item>
                </HasAccess>

                <HasAccess
                  roles={['super_usuario', 'branch_office_manager', 'administracion']}
                  permission="menu-reservation-forecast"
                >
                  <Menu.Item key="sub15" className="mt-0 mb-0 pl-20">
                    <Link
                      to="/reservas/pronostico"
                      onClick={() => handleDrawersVisibility('desktop')}
                    >
                      Pronóstico Asistencia
                    </Link>
                  </Menu.Item>
                </HasAccess>

              </Menu.ItemGroup>
            </HasAccess>

            <Menu.ItemGroup key="2" title="Exámenes">
              <HasAccess
                roles={['equipo_medico', 'equipo_psicologia', 'super_usuario', 'administracion',
                  'branch_office_manager', 'doctor', 'direccion_medica', 'equipo_medico_externo']}
                permission="menu-exams-main"
              >
                <Menu.Item key="sub15" className="mt-0 mb-0 pr-0">
                  <Link to="/examenes/buscar" onClick={() => handleDrawersVisibility('desktop')}>
                    Buscador de exámenes
                  </Link>
                </Menu.Item>
              </HasAccess>
              <HasAccess
                roles={['equipo_medico', 'equipo_psicologia', 'super_usuario', 'administracion',
                  'branch_office_manager', 'doctor', 'direccion_medica']}
                permission="menu-exams-main"
              >
                {
                  countryCode === 'PE' &&
                  <Menu.Item key="sub16" className="mt-0 mb-0 pr-0">
                    <Link to="/examenes/firma/buscar" onClick={() => handleDrawersVisibility('desktop')}>
                      Firma de exámenes
                    </Link>
                  </Menu.Item>
                }
              </HasAccess>
            </Menu.ItemGroup>

            <HasAccess
              roles={['doctor', 'super_usuario', 'administracion',
                'branch_office_manager', 'equipo_psicologia', 'direccion_medica']}
              permission="menu-consultations-main"
            >
              <Menu.ItemGroup key="3" title={t('privateLayout.medicalConsultation')}>
                <HasAccess
                  roles={['doctor', 'super_usuario', 'branch_office_manager',
                    'equipo_psicologia', 'direccion_medica', 'administracion']}
                  permission="menu-consultations-search"
                >
                  <Menu.Item key="sub21" className="mt-0 mb-0 pl-20" onClick={() => handleDrawersVisibility('desktop')}>
                    <Link to="/consulta-medica/buscar">
                      {t('privateLayout.dailyMedicalConsultation')}
                    </Link>
                  </Menu.Item>
                </HasAccess>

                <HasAccess
                  roles={['doctor', 'super_usuario', 'administracion', 'branch_office_manager',
                    'equipo_psicologia', 'direccion_medica']}
                  permission="menu-consultations-reports"
                >
                  <Menu.Item key="sub22" className="mt-0 mb-0 pl-20" onClick={() => handleDrawersVisibility('desktop')}>
                    <Link to="/consulta-medica/informes">
                      Informes
                    </Link>
                  </Menu.Item>
                </HasAccess>
              </Menu.ItemGroup>
            </HasAccess>

            <HasAccess
              roles={['doctor', 'super_usuario', 'administracion', 'branch_office_manager',
                'direccion_medica', 'equipo_medico', 'equipo_psicologia']}
              permission="menu-patients-search"
            >
              <Menu.Item key="4" title={t('privateLayout.patients')} className="pl-0">
                <Link to="/pacientes/buscar" onClick={() => handleDrawersVisibility('desktop')}>
                  {t('privateLayout.patients')}
                </Link>
              </Menu.Item>
            </HasAccess>
          </Menu>
        </Drawer>

        <Layout>
          <Content
            style={{
              minHeight: "100vh",
              marginTop: 0
            }}
          >
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default PrivateLayout;
